/**
 * 组织创建/编辑模态框组件
 * 负责组织的创建和编辑功能
 */

import React, { useEffect } from "react";
import { Modal, Form, Input, Select } from "antd";
import { MODAL_TYPES } from "../constants/organizationConstants";

const { TextArea } = Input;

const OrganizationModal = ({
    visible,
    modalType,
    selectedOrganization,
    organizationTree,
    onCancel,
    onSubmit
}) => {
    const [form] = Form.useForm();

    // 当模态框打开时，设置表单初始值
    useEffect(() => {
        if (visible) {
            if (modalType === MODAL_TYPES.CREATE) {
                form.resetFields();
                if (selectedOrganization) {
                    form.setFieldsValue({ parent_id: selectedOrganization.id });
                }
            } else if (modalType === MODAL_TYPES.EDIT && selectedOrganization) {
                form.setFieldsValue({
                    name: selectedOrganization.name,
                    description: selectedOrganization.description,
                    parent_id: selectedOrganization.parent_id,
                });
            }
        }
    }, [visible, modalType, selectedOrganization, form]);

    // 获取可选的父组织列表
    const getParentOptions = (tree, excludeId = null) => {
        const options = [];
        const traverse = (nodes) => {
            nodes.forEach((node) => {
                if (node.id !== excludeId) {
                    options.push({ value: node.id, label: node.name });
                    if (node.children) {
                        traverse(node.children);
                    }
                }
            });
        };
        traverse(tree);
        return options;
    };

    const handleSubmit = async (values) => {
        try {
            // 如果是创建子组织，确保parent_id被包含在提交数据中
            const submitData = { ...values };
            if (modalType === MODAL_TYPES.CREATE && selectedOrganization) {
                submitData.parent_id = selectedOrganization.id;
            }

            await onSubmit(submitData);
            form.resetFields();
        } catch (error) {
            // 错误处理由父组件负责
            console.error('Form submit error:', error);
        }
    };

    // 获取模态框标题
    const getModalTitle = () => {
        if (modalType === MODAL_TYPES.EDIT) {
            return "编辑组织";
        }
        if (modalType === MODAL_TYPES.CREATE) {
            return selectedOrganization ? `创建子组织 - ${selectedOrganization.name}` : "创建组织";
        }
        return "组织管理";
    };

    return (
        <Modal
            title={getModalTitle()}
            open={visible}
            onCancel={onCancel}
            onOk={() => form.submit()}
            okText="确定"
            cancelText="取消"
            destroyOnHidden
        >
            <Form 
                form={form} 
                layout="vertical" 
                onFinish={handleSubmit}
                preserve={false}
            >
                <Form.Item
                    name="name"
                    label="组织名称"
                    rules={[
                        { required: true, message: "请输入组织名称" },
                        { max: 200, message: "组织名称不能超过200个字符" },
                    ]}
                >
                    <Input placeholder="请输入组织名称" />
                </Form.Item>

                <Form.Item name="description" label="组织描述">
                    <TextArea 
                        placeholder="请输入组织描述" 
                        rows={3} 
                        maxLength={500} 
                        showCount 
                    />
                </Form.Item>

                {/* 只有在创建根组织或编辑组织时才显示父组织选择 */}
                {(modalType === MODAL_TYPES.CREATE && !selectedOrganization) || modalType === MODAL_TYPES.EDIT ? (
                    <Form.Item name="parent_id" label="父组织">
                        <Select
                            placeholder="请选择父组织（可选）"
                            allowClear
                            options={getParentOptions(organizationTree, selectedOrganization?.id)}
                        />
                    </Form.Item>
                ) : modalType === MODAL_TYPES.CREATE && selectedOrganization ? (
                    <>
                        {/* 隐藏的parent_id字段，确保值被提交 */}
                        <Form.Item name="parent_id" hidden>
                            <Input />
                        </Form.Item>
                        <Form.Item label="父组织">
                            <Input
                                value={selectedOrganization.name}
                                disabled
                                style={{ backgroundColor: '#f5f5f5' }}
                            />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                将在此组织下创建子组织
                            </div>
                        </Form.Item>
                    </>
                ) : null}
            </Form>
        </Modal>
    );
};

export default OrganizationModal;
